import { difference, intersectionWith } from 'lodash';
import { MNSproCloudApi } from '../..';
import { ServiceType } from './serviceTypes.interfaces';
import {
  classServiceDefinitionEntryToServiceType,
  classServiceDefinitionResponseToServiceType,
  classServicesDefinitionResponseToServiceType,
  serviceTypeToClassServiceDefinitionEntry,
  serviceTypeToClassServiceDefinitionResponse,
} from './serviceTypes.mappings';

const getApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.ClassRegisterApiFactory(config);
};

const options = {
  validateStatus: (status: number) =>
    (status >= 200 && status <= 299) || status === 404,
};

export class ServiceTypesService {
  static async createServiceType(
    serviceTypeToCreate: ServiceType,
    authToken: string,
    organizationId: string,
  ): Promise<ServiceType> {
    const api = getApi(authToken);
    const result = await api.createClassServiceDefinition(
      organizationId,
      serviceTypeToClassServiceDefinitionEntry(serviceTypeToCreate),
      undefined,
      options,
    );
    return classServiceDefinitionEntryToServiceType(result.data);
  }

  static async updateServiceType(
    serviceTypeToUpdate: ServiceType,
    authToken: string,
    organizationId: string,
  ): Promise<ServiceType> {
    const api = getApi(authToken);
    const result = await api.updateClassServiceDefinition(
      organizationId,
      serviceTypeToClassServiceDefinitionResponse(serviceTypeToUpdate),
      undefined,
      options,
    );

    return classServiceDefinitionEntryToServiceType(result.data);
  }

  static async deleteServiceType(
    serviceTypeId: string,
    authToken: string,
    organizationId: string,
  ): Promise<void> {
    const api = getApi(authToken);
    await api.deleteClassServiceDefinition(
      organizationId,
      serviceTypeId,
      undefined,
      options,
    );
  }

  static async getAllServiceTypes(
    authToken: string,
    organizationId: string,
  ): Promise<ServiceType[]> {
    const api = getApi(authToken);
    const result = await api.getAllClassServiceDefinitions(
      organizationId,
      undefined,
      options,
    );
    return result.data.map(classServicesDefinitionResponseToServiceType);
  }

  static async getLatestServiceType(
    serviceTypeId: string,
    organizationId: string,
    authToken: string,
  ): Promise<ServiceType> {
    const api = getApi(authToken);
    const result = await api.getLatestClassServicesDefinition(
      organizationId,
      serviceTypeId,
      undefined,
      options,
    );
    return classServiceDefinitionResponseToServiceType(result.data);
  }

  static async bulkUpdateServiceTypes(
    serviceTypes: ServiceType[],
    originalList: ServiceType[],
    authToken: string,
    organizationId: string,
  ): Promise<void> {
    //Create new
    const typesToCreate = serviceTypes.filter(type => !type.id);
    const api = getApi(authToken);
    for (const type of typesToCreate) {
      await api.createClassServiceDefinition(
        organizationId,
        serviceTypeToClassServiceDefinitionEntry(type),
        undefined,
        options,
      );
    }
    //update old
    const typesToUpdate = intersectionWith(
      serviceTypes,
      originalList,
      (updated, old) =>
        updated.id === old.id &&
        !(
          updated.description === old.description &&
          updated.fullName === old.fullName &&
          updated.shortName === old.shortName &&
          updated.isActive === old.isActive
        ),
    );

    for (const typeToUpdate of typesToUpdate) {
      await api.updateClassServiceDefinition(
        organizationId,
        serviceTypeToClassServiceDefinitionResponse(typeToUpdate),
        undefined,
        options,
      );
    }
    //delete

    const typesToDelete =
      difference(
        originalList.map(({ id }) => id as string),
        serviceTypes.map(({ id }) => id as string),
      ) ?? [];

    for (const idToDelete of typesToDelete) {
      await api.deleteClassServiceDefinition(
        organizationId,
        idToDelete,
        undefined,
        options,
      );
    }
  }
}
